import { useEffect } from 'react';
import { Shape } from '../types/Shape';
import { isYoutubeUrl } from '../utils/youtubeUtils';

interface UseKeyboardShortcutsProps {
  selectedShapes: string[];
  editingShape: string | null;
  shapes: Shape[];
  setShapes: React.Dispatch<React.SetStateAction<Shape[]>>;
  setSelectedShapes: React.Dispatch<React.SetStateAction<string[]>>;
  setShowResizeHandles: React.Dispatch<React.SetStateAction<boolean>>;
  setShowConnectionPoints: React.Dispatch<React.SetStateAction<boolean>>;
  copiedShapes: Shape[];
  setCopiedShapes: React.Dispatch<React.SetStateAction<Shape[]>>;
  onUndo?: () => void;
  onRedo?: () => void;
  isUserActionRef?: React.MutableRefObject<boolean>;
  onBeforeAction?: () => void;
  onExport?: () => void;
  onQuickExportPDF?: () => void;
  onQuickExportJPG?: () => void;
}

export const useKeyboardShortcuts = ({
  selectedShapes,
  editingShape,
  shapes,
  setShapes,
  setSelectedShapes,
  setShowResizeHandles,
  setShowConnectionPoints,
  copiedShapes,
  setCopiedShapes,
  onUndo,
  onRedo,
  isUserActionRef,
  onBeforeAction,
  onExport,
  onQuickExportPDF,
  onQuickExportJPG,
}: UseKeyboardShortcutsProps) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 텍스트 편집 중이면 무시
      if (editingShape) {
        return;
      }
      
      // 내보내기 메뉴 열기 (Ctrl+Shift+E)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'e') {
        e.preventDefault();
        if (onExport) {
          onExport();
        }
        return;
      }

      // 빠른 PDF 내보내기 (Ctrl+Shift+P)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        if (onQuickExportPDF) {
          onQuickExportPDF();
        }
        return;
      }

      // 빠른 JPG 내보내기 (Ctrl+Shift+J)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        if (onQuickExportJPG) {
          onQuickExportJPG();
        }
        return;
      }

      // Undo (Ctrl+Z)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (onUndo) {
          onUndo();
        }
        return;
      }
      
      // Redo (Ctrl+Y , Ctrl+R, 또는 Ctrl+Shift+Z)
      if (((e.ctrlKey || e.metaKey) && e.key === 'y') || 
          ((e.ctrlKey || e.metaKey) && e.key === 'r') || 
          ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z')) {
        e.preventDefault();
        if (onRedo) {
          onRedo();
        }
        return;
      }
      
      // 삭제 (Backspace, Delete)
      if (e.key === 'Backspace' || e.key === 'Delete') {
        if (selectedShapes.length > 0) {
          // 삭제 전 현재 상태를 히스토리에 저장
          if (onBeforeAction) {
            onBeforeAction();
          }
          if (isUserActionRef) {
            isUserActionRef.current = false; // 히스토리 저장 방지
          }
          setShapes(prev => {
            // 삭제될 화살표들 찾기
            const deletedArrows = prev.filter(
              shape => selectedShapes.includes(shape.id) && shape.type === 'arrow'
            );
            const deletedArrowIds = new Set(deletedArrows.map(a => a.id));
            
            // 삭제될 객체들 찾기
            const deletedShapes = prev.filter(
              shape => selectedShapes.includes(shape.id) && shape.type !== 'arrow'
            );
            const deletedShapeIds = new Set(deletedShapes.map(s => s.id));
            
            // 객체 삭제 및 connectedArrows 정리
            return prev
              .filter(shape => !selectedShapes.includes(shape.id))
              .map(shape => {
                // 화살표가 삭제된 경우: 연결된 객체의 connectedArrows에서 제거
                if (shape.connectedArrows && shape.connectedArrows.length > 0) {
                  const updatedConnectedArrows = shape.connectedArrows.filter(
                    arrowId => !deletedArrowIds.has(arrowId)
                  );
                  
                  // connectedArrows가 비어있으면 undefined로 설정
                  if (updatedConnectedArrows.length === 0) {
                    const { connectedArrows, ...rest } = shape;
                    return rest as Shape;
                  }
                  
                  return {
                    ...shape,
                    connectedArrows: updatedConnectedArrows
                  };
                }
                
                // 객체가 삭제된 경우: 연결된 화살표의 연결 정보 제거
                if (shape.type === 'arrow') {
                  const startDeleted = shape.startShapeId && deletedShapeIds.has(shape.startShapeId);
                  const endDeleted = shape.endShapeId && deletedShapeIds.has(shape.endShapeId);
                  
                  if (startDeleted || endDeleted) {
                    return {
                      ...shape,
                      startShapeId: startDeleted ? undefined : shape.startShapeId,
                      endShapeId: endDeleted ? undefined : shape.endShapeId,
                      startConnectionPoint: startDeleted ? undefined : shape.startConnectionPoint,
                      endConnectionPoint: endDeleted ? undefined : shape.endConnectionPoint,
                      startRelativeX: startDeleted ? undefined : shape.startRelativeX,
                      startRelativeY: startDeleted ? undefined : shape.startRelativeY,
                      endRelativeX: endDeleted ? undefined : shape.endRelativeX,
                      endRelativeY: endDeleted ? undefined : shape.endRelativeY,
                    };
                  }
                }
                
                return shape;
              });
          });
          setSelectedShapes([]);
          setShowResizeHandles(false);
          setShowConnectionPoints(false);
        }
        return;
      }

      // Arrow key 이동 (선택된 객체)
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && selectedShapes.length > 0) {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        setShapes(prev =>
          prev.map(shape =>
            selectedShapes.includes(shape.id)
              ? { ...shape, x: shape.x + dx, y: shape.y + dy }
              : shape
          )
        );
        return;
      }

      // 복사 (Ctrl+C)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        if (selectedShapes.length > 0) {
          const shapesToCopy = shapes.filter(shape => selectedShapes.includes(shape.id));
          setCopiedShapes(shapesToCopy);
        }
        return;
      }
      
      // 붙여넣기 (Ctrl+V)
      // 주의: paste 이벤트는 useClipboard에서 처리됨
      // 여기서는 keydown만 처리하므로, 실제 붙여넣기는 paste 이벤트에서 처리됨
      // 따라서 Ctrl+V 키만으로는 객체 붙여넣기를 할 수 없음
      // 대신 paste 이벤트 핸들러에서 객체 붙여넣기를 처리해야 함
      // 
      // 하지만 keydown 이벤트는 paste 이벤트보다 먼저 발생할 수 있으므로,
      // 여기서는 처리하지 않고 useClipboard에서 처리하도록 함
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        // keydown 이벤트에서는 처리하지 않음
        // paste 이벤트가 useClipboard에서 처리됨
        // copiedShapes가 있고, 클립보드에 이미지/유튜브가 없으면
        // useClipboard에서 객체 붙여넣기를 처리하지 않고 return하므로
        // 여기서도 처리하지 않음
        // 
        // 실제로는 paste 이벤트 핸들러에서만 처리해야 함
        return;
      }
      
      // 제자리 복제 (Ctrl+D)
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault();
        
        if (selectedShapes.length > 0) {
          // 복제 전 현재 상태를 히스토리에 저장
          if (onBeforeAction) {
            onBeforeAction();
          }
          if (isUserActionRef) {
            isUserActionRef.current = false; // 히스토리 저장 방지
          }
          
          const shapesToDuplicate = shapes.filter(shape => selectedShapes.includes(shape.id));
          const offset = 20;
          const duplicatedShapes = shapesToDuplicate.map(shape => ({
            ...shape,
            id: `${Date.now()}-${Math.random()}`,
            x: shape.x + offset,
            y: shape.y + offset,
            connectedArrows: undefined,
          }));
          
          setShapes(prev => [...prev, ...duplicatedShapes]);
          setSelectedShapes(duplicatedShapes.map(s => s.id));
          setShowResizeHandles(true);
        }
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedShapes, editingShape, shapes, copiedShapes, setCopiedShapes, setShapes, setSelectedShapes, setShowResizeHandles, setShowConnectionPoints, onUndo, onRedo, isUserActionRef, onBeforeAction, onExport, onQuickExportPDF, onQuickExportJPG]);
};

